// DNA CONNECT - Dashboard Inicial da Família
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { 
  Users, GraduationCap, CreditCard, MessageCircle, Calendar,
  FileText, Car, Bell, ChevronRight, AlertTriangle, Clock,
  CheckCircle, XCircle, Loader2, ExternalLink, BookOpen, Monitor
} from 'lucide-react';
import { toast } from 'sonner';
import { RematriculaHomeCard } from '@/pages/DnaConnect/Rematricula2027';
import { useNavigate } from 'react-router-dom';
import { EstudosCard } from './DnaConnectEstudos';
import { C, KpiCard, PageHeader } from '@/design-system/dna';
import { PayNowCard } from './financeiro/PayNowCard';
import { GoalNotice } from './GoalNotice';

const API_URL = window.location.origin;

function WelcomeKpis({ data, eventos, enabled, formatDate }) {
  const navigate = useNavigate();
  const filhos = data?.filhos || [];
  const avisos = data?.avisos_urgentes || [];
  const prox = (eventos.length ? eventos : (data?.proximos_eventos || []))[0];
  return (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-3" data-testid="familia-kpis">
      <KpiCard id="familia-filhos" icon={Users} label={filhos.length === 1 ? 'Filho vinculado' : 'Filhos vinculados'} value={filhos.length}
        hint={filhos.map((f) => f.name?.split(' ')[0]).filter(Boolean).join(', ') || 'Nenhum aluno vinculado'} color={C.purple}
        onClick={enabled.filhos === true ? () => navigate('/familia/filhos') : undefined} index={0} />
      <KpiCard id="familia-avisos" icon={Bell} label="Avisos recentes" value={avisos.length}
        hint={avisos[0]?.title || 'Nenhum aviso recente'} color={C.amber}
        onClick={enabled.comunicacao === true ? () => navigate('/familia/comunicacao') : undefined} index={2} />
      <KpiCard id="familia-evento" icon={Calendar} label="Próximo evento" value={prox ? formatDate(prox.event_date || prox.data_inicio || prox.start_date) : '—'}
        hint={prox ? (prox.titulo || prox.title) : 'Nenhum evento próximo'} color={C.magenta}
        onClick={enabled.calendario === true ? () => navigate('/familia/calendario') : undefined} index={3} />
    </div>
  );
}

export default function DnaConnectDashboard() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [eventosCalendario, setEventosCalendario] = useState([]);
  const [user, setUser] = useState(null);

  useEffect(() => {
    const userData = localStorage.getItem('familyUser');
    if (userData) {
      setUser(JSON.parse(userData));
    }
    loadDashboard();
    loadProximosEventos();
  }, []);

  const loadDashboard = async () => {
    try {
      const token = localStorage.getItem('familyToken');
      const response = await fetch(`${API_URL}/api/dnaconnect/dashboard?token=${token}`);
      
      if (!response.ok) throw new Error('Erro ao carregar dashboard');
      
      const result = await response.json();
      setData(result);
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  // Mesma fonte do Calendário (timezone-safe). NÃO criar cálculo próprio na Home.
  const loadProximosEventos = async () => {
    try {
      const res = await fetch(`${API_URL}/api/calendario/eventos/publico/proximos?limite=3`);
      if (!res.ok) return;
      const j = await res.json();
      setEventosCalendario(j.proximos || []);
    } catch (e) {
      // silencioso — fallback usa data.proximos_eventos
    }
  };

  // Função única de formatação (igual à página Calendário)
  const formatSchoolDate = (value) => {
    if (!value) return '';
    const m = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
    return m ? `${m[3]}/${m[2]}/${m[1]}` : String(value);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  // Módulos habilitados (do backend) - PADRÃO: TUDO BLOQUEADO
  const enabledModules = data?.modulos_habilitados || {
    inicio: true,
    filhos: false,
    pedagogico: false,
    financeiro: false,
    dnago: false,
    comunicacao: false,
    calendario: false,
    documentos: false,
    perfil: true
  };

  // Atalhos rápidos - filtrados pelos módulos habilitados
  const allShortcuts = [
    { label: 'Meus Filhos', icon: Users, path: '/familia/filhos', color: 'bg-blue-500', module: 'filhos' },
    { label: 'Boletim', icon: GraduationCap, path: '/familia/pedagogico', color: 'bg-green-500', module: 'pedagogico' },
    { label: 'Financeiro', icon: CreditCard, path: '/familia/financeiro', color: 'bg-purple-500', module: 'financeiro' },
    { label: 'DNA GO', icon: Car, path: '/familia/dnago', color: 'bg-orange-500', module: 'dnago' },
    { label: 'Mensagens', icon: MessageCircle, path: '/familia/comunicacao', color: 'bg-pink-500', module: 'comunicacao' },
    { label: 'Calendário', icon: Calendar, path: '/familia/calendario', color: 'bg-cyan-500', module: 'calendario' },
  ];

  // Filtrar apenas os atalhos cujos módulos estão EXPLICITAMENTE habilitados
  const shortcuts = allShortcuts.filter(s => enabledModules[s.module] === true);

  return (
    <div className="flex flex-col gap-5">
      {/* Saudação · padrão Dashboard DNA */}
      <PageHeader variant="hero" kicker="DNA Connect · NOVO DNA" title={data?.saudacao || 'Olá!'} testid="familia-header"
        subtitle="Acompanhe a rotina escolar, o financeiro e os avisos da sua família em um só lugar." />
      {enabledModules.financeiro === true && <PayNowCard />}
      <WelcomeKpis data={data} eventos={eventosCalendario} enabled={enabledModules} formatDate={formatSchoolDate} />
      {enabledModules.pedagogico === true && <GoalNotice />}

      {/* V6.0 · Card de Rematrícula */}
      <RematriculaCard />

      {/* V7.0 · DNA IA Estudos */}
      <EstudosCardsFamilia filhos={data?.filhos || []} />

      {/* Cards dos Filhos */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-extrabold" style={{ color: C.ink }}>Meus Filhos</h2>
          <Link to="/familia/filhos" className="text-purple-600 hover:underline text-sm flex items-center gap-1">
            Ver todos <ChevronRight className="w-4 h-4" />
          </Link>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {data?.filhos?.map((filho) => (
            <Card key={filho.id} className="hover:shadow-lg transition-shadow">
              <CardContent className="p-4">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-xl overflow-hidden">
                    <img src={filho.photo_url || "/default-avatar.png"} onError={(e) => { e.currentTarget.src = "/default-avatar.png"; }} alt="" className="w-16 h-16 rounded-xl object-cover" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900">{filho.name?.split(' ')[0]}</h3>
                    <p className="text-sm text-gray-500">{filho.class_name}</p>
                    <Badge 
                      variant={filho.situation === 'Ativo' ? 'default' : 'secondary'}
                      className="mt-1"
                    >
                      {filho.situation}
                    </Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Hub Educacional */}
      <div>
        <h2 className="text-lg font-extrabold mb-1" style={{ color: C.ink }}>Hub Educacional</h2>
        <p className="text-sm text-gray-500 mb-4">Acesse rapidamente suas plataformas de estudo</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <a href="https://classroom.google.com/" target="_blank" rel="noopener noreferrer"
            className="group block bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-md hover:border-purple-200 transition-all p-5">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#1FA34A] to-[#178A3D] flex items-center justify-center flex-shrink-0">
                <Monitor className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="font-semibold text-gray-900 group-hover:text-purple-700 transition-colors">Classroom</h3>
                  <ExternalLink className="w-3.5 h-3.5 text-gray-300 group-hover:text-purple-400 transition-colors" />
                </div>
                <p className="text-sm text-gray-500">Atividades, aulas e comunicados</p>
              </div>
            </div>
          </a>
          <a href="https://login.plurall.net/" target="_blank" rel="noopener noreferrer"
            className="group block bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-md hover:border-purple-200 transition-all p-5">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#7B3F98] to-[#3F1F5C] flex items-center justify-center flex-shrink-0">
                <BookOpen className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="font-semibold text-gray-900 group-hover:text-purple-700 transition-colors">Plurall</h3>
                  <ExternalLink className="w-3.5 h-3.5 text-gray-300 group-hover:text-purple-400 transition-colors" />
                </div>
                <p className="text-sm text-gray-500">Conteúdos e recursos de aprendizagem</p>
              </div>
            </div>
          </a>
        </div>
      </div>

      {/* Grid de Informações */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {/* Situação Financeira - apenas se módulo EXPLICITAMENTE habilitado */}
        {enabledModules.financeiro === true && (
          <Card className={`border-l-4 ${data?.financeiro?.total_vencido > 0 ? 'border-l-red-500' : 'border-l-green-500'}`}>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
                <CreditCard className="w-4 h-4" />
                Situação Financeira
              </CardTitle>
            </CardHeader>
            <CardContent>
              {data?.financeiro?.total_vencido > 0 ? (
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-red-600">
                    <XCircle className="w-5 h-5" />
                    <span className="font-semibold">Pendências</span>
                  </div>
                  <p className="text-sm text-gray-600">
                    Valor vencido: <span className="font-bold text-red-600">
                      R$ {data.financeiro.total_vencido.toFixed(2)}
                    </span>
                  </p>
                </div>
              ) : (
                <div className="flex items-center gap-2 text-green-600">
                  <CheckCircle className="w-5 h-5" />
                  <span className="font-semibold">Em dia</span>
                </div>
              )}
              
              {data?.financeiro?.total_pendente > 0 && (
                <p className="text-sm text-gray-500 mt-2">
                  {data.financeiro.titulos_abertos} título(s) pendente(s)
                </p>
              )}
              
              <Link to="/familia/financeiro">
                <Button variant="outline" size="sm" className="w-full mt-3">
                  Ver detalhes
                </Button>
              </Link>
            </CardContent>
          </Card>
        )}

        {/* Avisos Urgentes */}
        <Card className="border-l-4 border-l-[#F7953A]">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
              <Bell className="w-4 h-4" />
              Avisos Recentes
            </CardTitle>
          </CardHeader>
          <CardContent>
            {data?.avisos_urgentes?.length > 0 ? (
              <div className="space-y-2">
                {data.avisos_urgentes.slice(0, 3).map((aviso, idx) => (
                  <div key={idx} className="flex items-start gap-2 text-sm">
                    <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700 line-clamp-2">{aviso.title}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500">Nenhum aviso recente</p>
            )}
            
            <Link to="/familia/comunicacao">
              <Button variant="outline" size="sm" className="w-full mt-3">
                Ver mensagens
              </Button>
            </Link>
          </CardContent>
        </Card>

        {/* Próximos Eventos */}
        <Card className="border-l-4 border-l-[#D6336C]">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              Próximos Eventos
            </CardTitle>
          </CardHeader>
          <CardContent>
            {(eventosCalendario.length > 0 ? eventosCalendario : (data?.proximos_eventos || [])).length > 0 ? (
              <div className="space-y-2">
                {(eventosCalendario.length > 0 ? eventosCalendario : data.proximos_eventos).slice(0, 3).map((evento, idx) => (
                  <div key={idx} className="flex items-start gap-2 text-sm">
                    <Clock className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <span className="text-gray-700 block">{evento.titulo || evento.title}</span>
                      <span className="text-gray-400 text-xs">
                        {formatSchoolDate(evento.event_date || evento.data_inicio || evento.start_date)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500">Nenhum evento próximo</p>
            )}
            
            <Link to="/familia/calendario">
              <Button variant="outline" size="sm" className="w-full mt-3">
                Ver calendário
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>

      {/* Atalhos Rápidos */}
      <div>
        <h2 className="text-lg font-bold text-gray-800 mb-4">Acesso Rápido</h2>
        <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
          {shortcuts.map((shortcut) => {
            const Icon = shortcut.icon;
            return (
              <Link
                key={shortcut.label}
                to={shortcut.path}
                className="flex flex-col items-center gap-2 p-4 bg-white rounded-xl border hover:shadow-lg transition-all hover:-translate-y-1"
              >
                <div className={`w-12 h-12 ${shortcut.color} rounded-xl flex items-center justify-center`}>
                  <Icon className="w-6 h-6 text-white" />
                </div>
                <span className="text-xs font-medium text-gray-700 text-center">{shortcut.label}</span>
              </Link>
            );
          })}
        </div>
      </div>

      {/* Notificações */}
      {data?.notificacoes?.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Bell className="w-4 h-4 text-purple-600" />
              Notificações não lidas
              <Badge className="bg-purple-100 text-purple-700">{data.notificacoes_nao_lidas}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {data.notificacoes.map((notif, idx) => (
                <div key={idx} className="flex items-center gap-3 p-2 bg-purple-50 rounded-lg">
                  <div className="w-2 h-2 bg-purple-600 rounded-full" />
                  <span className="text-sm text-gray-700">{notif.message || notif.title}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}


// V6.0 · Card de Rematrícula no dashboard da família
function EstudosCardsFamilia({ filhos }) {
  const navigate = useNavigate();
  if (!filhos?.length) return null;
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3" data-testid="estudos-cards-familia">
      {filhos.slice(0, 4).map((f) => (
        <EstudosCard key={f.id} studentId={f.id} onOpen={() => navigate('/familia/estudos')} />
      ))}
    </div>
  );
}

// V9.2 · card premium da Rematrícula 2027 (motor único do DNA Matrículas · token da família)
function RematriculaCard() {
  return <RematriculaHomeCard />;
}
